<script>
import { mapGetters } from 'vuex';
import { useVuelidate } from '@vuelidate/core';
import { minValue } from '@vuelidate/validators';
import { useAlert } from 'dashboard/composables';
import { useConfig } from 'dashboard/composables/useConfig';
import SettingsSection from '../../../../../components/SettingsSection.vue';
import Switch from 'v3/components/Form/Switch.vue';

export default {
  components: {
    SettingsSection,
    Switch,
  },
  props: {
    inbox: {
      type: Object,
      default: () => ({}),
    },
  },
  setup() {
    const { isEnterprise } = useConfig();

    return { v$: useVuelidate(), isEnterprise };
  },
  data() {
    return {
      selectedAgents: [],
      isAgentListUpdating: false,
      enableAutoAssignment: false,
      maxAssignmentLimit: null,
    };
  },
  computed: {
    ...mapGetters({
      agentList: 'agents/getAgents',
    }),
    maxAssignmentLimitErrors() {
      if (this.v$.maxAssignmentLimit.$error) {
        return this.$t(
          'INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_RANGE_ERROR'
        );
      }
      return '';
    },
  },
  watch: {
    inbox() {
      this.setDefaults();
    },
  },
  mounted() {
    this.setDefaults();
  },
  methods: {
    setDefaults() {
      this.enableAutoAssignment = this.inbox.enable_auto_assignment;
      this.maxAssignmentLimit =
        this.inbox?.auto_assignment_config?.max_assignment_limit || null;
      this.fetchAttachedAgents();
    },
    async fetchAttachedAgents() {
      try {
        const response = await this.$store.dispatch('inboxMembers/get', {
          inboxId: this.inbox.id,
        });
        const {
          data: { payload: inboxMembers },
        } = response;
        this.selectedAgents = inboxMembers;
      } catch (error) {
        //  Handle error
      }
    },
    handleEnableAutoAssignment() {
      this.updateInbox();
    },
    async updateAgents() {
      const agentList = this.selectedAgents.map(el => el.id);
      this.isAgentListUpdating = true;
      try {
        await this.$store.dispatch('inboxMembers/create', {
          inboxId: this.inbox.id,
          agentList,
        });
        useAlert(this.$t('AGENT_MGMT.EDIT.API.SUCCESS_MESSAGE'), {type: 'success'});
      } catch (error) {
        useAlert(this.$t('AGENT_MGMT.EDIT.API.ERROR_MESSAGE'), {type: 'error'});
      }
      this.isAgentListUpdating = false;
    },
    async updateInbox() {
      try {
        const payload = {
          id: this.inbox.id,
          formData: false,
          enable_auto_assignment: this.enableAutoAssignment,
          auto_assignment_config: {
            max_assignment_limit: this.maxAssignmentLimit,
          },
        };
        await this.$store.dispatch('inboxes/updateInbox', payload);
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'), {type: 'success'});
      } catch (error) {
        useAlert(this.$t('INBOX_MGMT.EDIT.API.SUCCESS_MESSAGE'), {type: 'success'});
      }
    },
  },
  validations: {
    selectedAgents: {
      isEmpty() {
        return !!this.selectedAgents.length;
      },
    },
    maxAssignmentLimit: {
      minValue: minValue(1),
    },
  },
};
</script>

<template>
  <div class="flex flex-col gap-3 bg-n-gray-1 dark:bg-transparent min-h-[85vh] rounded-lg">
    <div class="dark:bg-n-vars-br-menu/30 p-0 md:p-6 rounded-lg flex-flex-col gap-6">
      <div class="flex gap-3 items-start justify-between">
        <div class="flex flex-col gap-1">
          <h3 class="text-base text-woot-500 dark:text-n-gray-1 mb-0 font-semibold">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS') }}
          </h3>
          <p class="text-base text-slate-700 dark:text-n-gray-5">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.INBOX_AGENTS_SUB_TEXT') }}
          </p>
        </div>
        <woot-submit-button class="input-base !bg-n-bluegray-4 !text-white rounded-lg !h-[34px] !min-w-fit"
          :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')" :loading="isAgentListUpdating" @click="updateAgents" />
      </div>
      <div class="w-full">
        <multiselect v-model="selectedAgents" :options="agentList" track-by="id" label="name" multiple
          :close-on-select="false" :clear-on-select="false" hide-selected placeholder="Pick some" selected-label
          :select-label="$t('FORMS.MULTISELECT.ENTER_TO_SELECT')"
          :deselect-label="$t('FORMS.MULTISELECT.ENTER_TO_REMOVE')" @select="v$.selectedAgents.$touch" />
      </div>
    </div>

    <div class="dark:bg-n-vars-br-menu/30 p-0 md:p-6 rounded-lg flex-flex-col gap-6">
      <SettingsSection :title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT')"
        :sub-title="$t('INBOX_MGMT.SETTINGS_POPUP.AGENT_ASSIGNMENT_SUB_TEXT')" :show-border="false">

        <div class="w-full flex flex-col gap-3">
          <div class="flex items-center justify-between w-full md:w-2/5">
            <label class="block text-sm font-medium text-gray-700 dark:text-gray-n-gray-25">
              {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT') }}
            </label>
            <Switch v-model="enableAutoAssignment" @change="handleEnableAutoAssignment" />
          </div>
          <p class="mt-6 text-sm text-slate-600 dark:text-slate-400">
            {{ $t('INBOX_MGMT.SETTINGS_POPUP.AUTO_ASSIGNMENT_SUB_TEXT') }}
          </p>
        </div>

        <div v-if="enableAutoAssignment" class="max-assignment-container">
          <div class="flex gap-3 items-start justify-between">
            <div class="flex flex-col gap-1">
              <label class="text-base text-woot-500 dark:text-n-gray-1 mb-0 font-semibold" for="maxAssignmentLimit">{{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT') }}</label>
              <p class="text-base not-italic text-slate-600 dark:text-n-gray-5">
                {{ $t('INBOX_MGMT.AUTO_ASSIGNMENT.MAX_ASSIGNMENT_LIMIT_SUB_TEXT') }}
              </p>
            </div>
            <woot-submit-button class="input-base !bg-n-bluegray-4 !text-white rounded-lg !h-[34px] !min-w-fit"
              :button-text="$t('INBOX_MGMT.SETTINGS_POPUP.UPDATE')" :disabled="v$.maxAssignmentLimit.$invalid"
              @click="updateInbox" />
          </div>
          <woot-input v-model="maxAssignmentLimit" type="number" :class="{ error: v$.maxAssignmentLimit.$error }"
            :error="maxAssignmentLimitErrors" @blur="v$.maxAssignmentLimit.$touch" />
        </div>
      </SettingsSection>

    </div>
  </div>
</template>

<style scoped lang="scss">
@import 'dashboard/assets/scss/variables';
@import 'dashboard/assets/scss/mixins';

.max-assignment-container {
  padding-top: var(--space-slab);
  padding-bottom: var(--space-slab);
}
</style>